import React from 'react';
import ReactDOM from 'react-dom';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const SpecsTemplate = React.forwardRef(
    ({ lang, vehicle, models, unifiedCats, getSpecValue, trans }, ref) => {
        const isRtl = lang === 'ar';
        const dir = isRtl ? 'rtl' : 'ltr';

        const page = {
            width: '794px',   // A4 @ 96 dpi
            fontFamily: "'Cairo', 'Univers Next Arabic', 'Segoe UI', Arial, sans-serif",
            fontSize: '10px',
            direction: dir,
            backgroundColor: '#fff',
            padding: '40px',
            boxSizing: 'border-box',
            letterSpacing: 'normal',
            textRendering: 'optimizeLegibility',
        };

        const green = '#18a94e';

        // Chunk models into groups of 4 for table stacking
        const modelChunks = [];
        const chunkSize = 4;
        for (let i = 0; i < models.length; i += chunkSize) {
            modelChunks.push(models.slice(i, i + chunkSize));
        }

        return (
            <div ref={ref} style={page}>

                {/* ── Header ──────────────────────────────────────────────── */}
                <div style={{
                    display: 'flex',
                    flexDirection: isRtl ? 'row-reverse' : 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `2px solid ${green}`,
                    paddingBottom: '15px',
                    marginBottom: '20px',
                    direction: dir,
                }}>
                    <div style={{ width: '140px', fontSize: '16px', color: green, fontWeight: 'bold', textAlign: isRtl ? 'right' : 'left' }}>
                        Hassan Jameel
                    </div>
                    <div style={{ textAlign: isRtl ? 'right' : 'left', direction: dir }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a' }}>
                            {vehicle?.brand} {vehicle?.group} {vehicle?.year}
                        </div>
                        <div style={{ fontSize: '12px', color: green, fontWeight: 'bold', marginTop: '4px' }}>
                            {trans ? trans('vehicales:detailed_specs', "المواصفات التفصيلية") : "المواصفات التفصيلية"}
                        </div>
                    </div>
                </div>

                {/* ── Vehicle highlights ──────────────────────────────────── */}
                <div style={{
                    display: 'flex',
                    flexDirection: isRtl ? 'row-reverse' : 'row',
                    justifyContent: 'space-between',
                    background: '#f8f8f8',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px',
                    direction: dir,
                }}>
                    {[
                        { label: trans ? trans('vehicales:model_label', "الموديل") : "الموديل", value: vehicle?.group?.toUpperCase() },
                        { label: trans ? trans('vehicales:year_label', "سنة الصنع") : "سنة الصنع", value: vehicle?.year },
                        { label: trans ? trans('vehicales:brand_label', "الماركة") : "الماركة", value: vehicle?.brand },
                    ].map(({ label, value }, i) => (
                        <div key={i} style={{ width: '30%', textAlign: 'center' }}>
                            <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>{label}</div>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>{value}</div>
                        </div>
                    ))}
                </div>

                {/* ── Spec sections ────────────────────────────────────────── */}
                {unifiedCats.map((cat, catIdx) => {
                    // Check if any visible rows exist for ANY model in this category
                    const visibleRows = cat.rows.filter(rowLabel =>
                        models.some(m => {
                            const v = getSpecValue(m.productTarget, cat.title, rowLabel);
                            return v !== null && v !== '—';
                        })
                    );
                    if (!visibleRows.length) return null;

                    return (
                        <div key={catIdx} style={{ marginTop: '15px' }}>

                            {/* Category Banner */}
                            <div style={{
                                background: green,
                                color: '#fff',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                marginBottom: '12px',
                                display: 'flex',
                                flexDirection: isRtl ? 'row-reverse' : 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                direction: dir,
                                pageBreakAfter: 'avoid'
                            }}>
                                <span style={{ fontWeight: 'bold', fontSize: '11px', textAlign: isRtl ? 'right' : 'left', width: '100%' }}>
                                    {cat.title}
                                </span>
                            </div>

                            {/* Stacked tables (one per 4 models) */}
                            {modelChunks.map((chunk, chunkIdx) => (
                                <div key={chunkIdx} style={{ marginBottom: '20px', pageBreakInside: 'avoid' }}>
                                    {modelChunks.length > 1 && (
                                        <div style={{ fontSize: '8px', color: '#999', marginBottom: '4px', textAlign: isRtl ? 'left' : 'right' }}>
                                            {isRtl ? `مجموعة ${chunkIdx + 1}` : `Group ${chunkIdx + 1}`}
                                        </div>
                                    )}
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'separate',
                                        borderSpacing: '0',
                                        border: '1px solid #eee',
                                        borderRadius: '4px',
                                        fontSize: '9px',
                                        direction: dir,
                                    }}>
                                        <thead>
                                            <tr style={{ background: '#f0f9f1', borderBottom: `1.5px solid ${green}` }}>
                                                <th style={{
                                                    padding: '8px 6px',
                                                    textAlign: isRtl ? 'right' : 'left',
                                                    width: '28%',
                                                    borderInlineEnd: '1px solid #eee',
                                                    fontWeight: 'bold',
                                                    color: '#444',
                                                }}>
                                                    {trans ? trans('vehicales:spec_label', "المواصفة") : "المواصفة"}
                                                </th>
                                                {chunk.map((m, mIdx) => (
                                                    <th key={mIdx} style={{
                                                        padding: '8px 6px',
                                                        textAlign: 'center',
                                                        fontWeight: 'bold',
                                                        color: m.hybird ? '#1b8f47' : '#1565c0',
                                                    }}>
                                                        {m.modelDescription}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((rowLabel, rIdx) => (
                                                <tr key={rIdx} style={{
                                                    background: rIdx % 2 === 0 ? '#fafafa' : '#fff',
                                                    borderBottom: '1px solid #eee',
                                                }}>
                                                    <td style={{
                                                        padding: '7px 6px',
                                                        textAlign: isRtl ? 'right' : 'left',
                                                        fontWeight: 'bold',
                                                        color: '#444',
                                                        borderInlineEnd: '1px solid #eee',
                                                        lineHeight: '1.6',
                                                        letterSpacing: '0px',
                                                        wordBreak: 'break-word',
                                                    }}>
                                                        {rowLabel}
                                                    </td>
                                                    {chunk.map((m, vIdx) => {
                                                        const v = getSpecValue(m.productTarget, cat.title, rowLabel);
                                                        const isCheck = (v === '✓' || (v && v.toLowerCase && v.toLowerCase() === 'check'));
                                                        return (
                                                            <td key={vIdx} style={{
                                                                padding: '7px 6px',
                                                                textAlign: 'center',
                                                                color: isCheck ? green : '#333',
                                                                lineHeight: '1.6',
                                                                fontWeight: isCheck ? 'bold' : 'normal'
                                                            }}>
                                                                {isCheck ? '✓' : (v || '—')}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    );
                })}

                {/* ── Footer ──────────────────────────────────────────────── */}
                <div style={{
                    marginTop: '40px',
                    borderTop: '2px solid #f0f0f0',
                    paddingTop: '20px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    direction: dir,
                }}>
                    {isRtl ? (
                        <>
                            <div style={{ textAlign: 'center', width: '130px', borderLeft: '1px solid #eee', padding: '0 15px' }}>
                                <p style={{ fontSize: '9px', fontWeight: 'bold', color: '#18a94e', marginBottom: '8px', lineHeight: '1.4' }}>
                                    حمل تطبيقنا لتجربة خدمة أفضل
                                </p>
                                <img src="/assets/qrcode.jfif" style={{ width: '85px', height: '85px', borderRadius: '8px', border: '1px solid #ddd' }} alt="QR" />
                            </div>
                            <div style={{ textAlign: 'right', flex: 1, paddingRight: '15px' }}>
                                <div style={{ color: green, fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>920018996</div>
                                <div style={{ color: '#555', fontSize: '10px', fontWeight: 'bold' }}>
                                    {trans ? trans('vehicales:company_name', "شركة حسن جميل للسيارات") : "شركة حسن جميل للسيارات"}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ textAlign: 'left', flex: 1 }}>
                                <div style={{ color: green, fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>920018996</div>
                                <div style={{ color: '#555', fontSize: '10px', fontWeight: 'bold' }}>
                                    {trans ? trans('vehicales:company_name', "شركة حسن جميل للسيارات") : "شركة حسن جميل للسيارات"}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center', width: '130px', borderLeft: '1px solid #eee', padding: '0 15px' }}>
                                <p style={{ fontSize: '9px', fontWeight: 'bold', color: '#18a94e', marginBottom: '8px', lineHeight: '1.4' }}>
                                    Download our app for better service experience
                                </p>
                                <img src="/assets/qrcode.jfif" style={{ width: '85px', height: '85px', borderRadius: '8px', border: '1px solid #ddd' }} alt="QR" />
                            </div>
                        </>
                    )}
                </div>

            </div>
        );
    }
);

async function captureAsPDF(element, vehicle) {
    const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
            const el = clonedDoc.querySelector('[style*=" Cairo "]');
            if (el) el.style.letterSpacing = '0px';
        }
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height / canvas.width) * imgW;
    const imgData = canvas.toDataURL('image/png', 1.0);

    let remaining = imgH;
    let offset = 0;

    while (remaining > 0) {
        pdf.addImage(imgData, 'PNG', 0, offset, imgW, imgH, '', 'FAST');
        remaining -= pageH;
        offset -= pageH;
        if (remaining > 1) pdf.addPage();
    }
    return pdf;
}

export const generateVehicleSpecsPDF = async (props) => {
    return new Promise((resolve, reject) => {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.top = '-9999px';
        div.style.left = '-9999px';
        div.style.pointerEvents = 'none';
        div.style.zIndex = '-9999';
        document.body.appendChild(div);

        const ref = React.createRef();

        ReactDOM.render(<SpecsTemplate ref={ref} {...props} />, div, async () => {
            try {
                await new Promise(r => setTimeout(r, 1500));
                const pdf = await captureAsPDF(ref.current, props.vehicle);
                const vehicleGroup = props.vehicle?.group || 'vehicle';
                pdf.save(`${vehicleGroup}-specs.pdf`);
                resolve();
            } catch (err) {
                console.error("Error generating PDF:", err);
                reject(err);
            } finally {
                ReactDOM.unmountComponentAtNode(div);
                document.body.removeChild(div);
            }
        });
    });
};



